import type { StringifiableRecord } from 'query-string';
import queryString from 'query-string';
import { WORKFLOWS_BASE_URL } from '~/elc/features/workflows/constants';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';

/** Should be 1:1 with the non-api routes in routes.rb */
const Urls = {
  eventOverview: (eventId: string, pathAssignmentId?: string, sessionId?: string, parentAssignmentIds?: string[]) =>
    queryStringifyUrl(`/events/${eventId}`, {
      path_assignment_id: pathAssignmentId,
      parent_assignment_ids: parentAssignmentIds,
      session_id: sessionId,
    }),

  register: '/register',

  learnerHome: '/guides',

  guidePreview: (id, universityId, universityFolderId) =>
    `/guides/${id}/preview${uId(universityId, universityFolderId)}`,
  guidePreviewInPath: (
    guideId: string,
    pathId: string,
    parentPathIds?: string[],
    universityId?: string | null,
    universityFolderId?: string | null,
  ) =>
    queryStringifyUrl(`/guides/${guideId}/preview`, {
      series_id: pathId,
      parent_path_ids: parentPathIds,
      university_id: universityId,
      university_folder_id: universityFolderId,
    }),
  guideStart: (id, universityId?: string, universityFolderId?: string) =>
    `/guides/${id}/start${uId(universityId, universityFolderId)}`,
  guideContinueAssignment: (guideAssignmentId, universityId?: string, universityFolderId?: string) =>
    `/guide_assignments/${guideAssignmentId}${uId(universityId, universityFolderId)}`,
  guideContinueAssignmentInPath: (
    guideAssignmentId: string,
    pathAssignmentId: string,
    parentAssignmentIds?: string[],
  ) =>
    queryStringifyUrl(`/guide_assignments/${guideAssignmentId}/path_assignment_id/${pathAssignmentId}`, {
      parent_assignment_ids: parentAssignmentIds,
    }),

  // @ts-expect-error - Expected 2 arguments, but got 1.
  resource: (id, universityId, uResourceId) => `/resources/${id}${uId(universityId)}&uresource_id=${uResourceId}`,
  // @ts-expect-error - Expected 2 arguments, but got 1.
  resourcePreview: (id, universityId) => `/resources/${id}/preview${uId(universityId)}`,
  resourcePreviewInPath: (
    resourceId: string,
    pathId: string,
    parentPathIds?: string[],
    universityId?: string | null,
    universityFolderId?: string | null,
  ) =>
    queryStringifyUrl(`/resources/${resourceId}/preview`, {
      series_id: pathId,
      parent_path_ids: parentPathIds,
      university_id: universityId,
      university_folder_id: universityFolderId,
    }),
  resourceContinueAssignmentInPath: (
    resourceId: string,
    resourceAssignmentId: string,
    parentAssignmentIds?: string[],
  ) =>
    queryStringifyUrl(`/resources/${resourceId}`, {
      resource_assignment_id: resourceAssignmentId,
      parent_assignment_ids: parentAssignmentIds,
    }),
  trainingSeriesUserAssignments: (id) => `/training_series/${id}/user_assignments`,

  challengePreview: (id, universityId, universityFolderId) =>
    `/challenges/${id}/preview${uId(universityId, universityFolderId)}`,
  challengePreviewInPath: (
    challengeId: string,
    pathId: string,
    parentPathIds?: string[],
    universityId?: string | null,
    universityFolderId?: string | null,
  ) =>
    queryStringifyUrl(`/challenges/${challengeId}/preview`, {
      series_id: pathId,
      parent_path_ids: parentPathIds,
      university_id: universityId,
      university_folder_id: universityFolderId,
    }),
  challengeStart: (id) => `/challenges/${id}/start_from_share_link`,
  challengeNewOrGoToFromUniversity: (universityId, challengeId) =>
    `/universities/${universityId}/challenge/${challengeId}/assignment`,
  challengeContinueAssignment: (challengeAssignmentId, universityId?: string, universityFolderId?: string) =>
    `/challenge_assignments/${challengeAssignmentId}${uId(universityId, universityFolderId)}`,
  challengeContinueAssignmentInPath: (challengeAssignmentId: string, parentAssignmentIds?: string[]) =>
    queryStringifyUrl(`/challenge_assignments/${challengeAssignmentId}`, {
      parent_assignment_ids: parentAssignmentIds,
    }),

  pathPreview: (pathId: string, universityId?: string, universityFolderId?: string) =>
    `/paths/${pathId}/preview${uId(universityId, universityFolderId)}`,
  pathStart: (pathId: string, universityId?: string, universityFolderId?: string) =>
    `/paths/${pathId}/start${uId(universityId, universityFolderId)}`,
  pathContinueAssignment: (pathAssignmentId: string, universityId?: string, universityFolderId?: string) =>
    `/series_assignments/${pathAssignmentId}${uId(universityId, universityFolderId)}`,
  pathContinueAssignmentInPath: (pathAssignmentId: string, parentAssignmentIds?: string[]) =>
    queryStringifyUrl(`/series_assignments/${pathAssignmentId}`, {
      parent_assignment_ids: parentAssignmentIds,
    }),
  pathPreviewInPath: (
    pathId: string,
    parentPathIds: string[] = [],
    universityId?: string | null,
    universityFolderId?: string | null,
  ) =>
    queryStringifyUrl(`/paths/${pathId}/preview`, {
      parent_path_ids: parentPathIds,
      university_id: universityId,
      university_folder_id: universityFolderId,
    }),

  scormPreview: (documentObjectMediaId) => `/scorm/${documentObjectMediaId}/preview_v2`,

  topLevelContentPreview: (tlcType, tlcId, universityId, universityFolderId) =>
    `/${tlcType}/${tlcId}/preview${uId(universityId, universityFolderId)}`,
  topLevelContentPreviewInPath: (
    tlcType: string,
    tlcId: string,
    pathId: string,
    parentPathIds?: string[],
    universityId?: string | null,
    universityFolderId?: string | null,
  ) =>
    queryStringifyUrl(`/${tlcType}/${tlcId}/preview`, {
      series_id: pathId,
      parent_path_ids: parentPathIds,
      university_id: universityId,
      university_folder_id: universityFolderId,
    }),
  topLevelContentStartOrContinueAssignment: (tlcType, tlcId, universityId?: string, universityFolderId?: string) =>
    `/${tlcType}/${tlcId}${uId(universityId, universityFolderId)}`,
  topLevelContentStartContentProviderContentInPath: (tlcId, pathId) =>
    `/content_provider_content/${tlcId}?series_id=${pathId}`,
  topLevelContentContinueAssignmentInPath: (
    tlcType: string,
    tlcId: string,
    pathAssignmentId: string,
    parentAssignmentIds?: string[],
  ) =>
    queryStringifyUrl(`/${tlcType}/${tlcId}`, {
      training_series_user_assignment_id: pathAssignmentId,
      parent_assignment_ids: parentAssignmentIds,
    }),

  university: (id, uFolderId) => `/universities/${id}${uFolderId ? `?university_folder_id=${uFolderId}` : ''}`,

  academyGuide: (baseExternalUrl, id) => `${baseExternalUrl}/training/${id}/overview`,
  academyResource: (baseExternalUrl, id) => `${baseExternalUrl}/training/${id}/register`,
  academyTopLevelContent: (baseExternalUrl, id) => `${baseExternalUrl}/training/${id}/register`,

  admin: {
    path: (id) => `/admin/paths/${id}`,
    pathArchived: (id) => `/admin/paths/${id}/archived`,
    pathEdit: (id) => `/admin/paths/${id}/edit`,
    pathPreview: (pathId: string, parentPathIds: string[] = []) =>
      queryStringifyUrl(`/admin/paths/${pathId}/preview`, {
        // Remove the current path id if its also included in the parent path ids
        parent_path_ids: parentPathIds.filter((id) => id !== pathId),
      }),

    guideEdit: (id) => `/admin/guides/${id}/edit`,
    guidePreview: (id) => `/admin/guides/${id}/preview`,
    guidePreviewInPath: (guideId: string, pathId: string, parentPathIds?: string[]) =>
      queryStringifyUrl(`/admin/guides/${guideId}/preview`, {
        series_id: pathId,
        parent_path_ids: parentPathIds,
      }),
    guideTaskPreview: (id) => `/admin/tasks/${id}/preview`,

    challenge: (id) => `admin/challenges/${id}`,
    challengeEdit: (id) => `/admin/challenges/${id}/edit`,
    challengePreview: (id) => `/admin/challenges/${id}/preview`,
    challengePreviewInPath: (challengeId: string, pathId: string, parentPathIds?: string[]) =>
      queryStringifyUrl(`/admin/challenges/${challengeId}/preview`, {
        series_id: pathId,
        parent_path_ids: parentPathIds,
      }),

    pathPreviewInPath: (id: string, parentPathIds?: string[]) =>
      queryStringifyUrl(`/admin/paths/${id}/preview`, { parent_path_ids: parentPathIds }),

    resourceEdit: (id) => `/admin/resources/${id}`,
    resourcePreview: (id) => `/admin/resources/${id}/preview`,
    resourcePreviewInPath: (resourceId: string, pathId: string, parentPathIds?: string[]) =>
      queryStringifyUrl(`/admin/resources/${resourceId}/preview`, {
        series_id: pathId,
        parent_path_ids: parentPathIds,
      }),

    taskPreview: (id, seriesId) => `/admin/tasks/${id}/preview?series_id=${seriesId}`,
    topLevelContent: (id, contentType) => `/admin/content/${contentType}/${id}`,
    topLevelContentPreview: (id, contentType) => `/admin/content/${contentType}/${id}/edit`, // preview same page as edit for TLC
    university: (id, uFolderId) => `/admin/universities/${id}${uFolderId ? `?university_folder_id=${uFolderId}` : ''}`,

    reviewExternalAssignment: (academyId, contentId, contentType, assignmentId) => {
      let urlContentType;

      switch (contentType) {
        case AssetPickerTypes.GUIDE:
          urlContentType = 'guides';
          break;
        case AssetPickerTypes.RESOURCE:
          urlContentType = 'resources';
          break;
        default:
          return '';
      }

      return `/admin/academies/${academyId}/assignments/${assignmentId}/${urlContentType}/${contentId}`;
    },
    academyIndividualTrainingEnrollments: (academyId, training_type, training_id) =>
      `/admin/academies/${academyId}/trainings/${training_type}/${training_id}`,
    academyIndividualCertificationEnrollments: (academyId, academyCertificationId) =>
      `/admin/academies/${academyId}/certification_enrollments/${academyCertificationId}`,
    academyIndividualPathEnrollments: (academyId, academyPathId) =>
      `/admin/academies/${academyId}/path_enrollments/${academyPathId}`,
    workflow: (workflowId) => `${WORKFLOWS_BASE_URL}/${workflowId}`,

    // Agents
    agents: () => '/admin/agents',
    agent: (slug) => `/admin/agents/${slug}`,
    closedWonAgent: () => '/admin/agents/closed-won',
    competitiveIntelAgent: () => '/admin/agents/competitive',
    roiAgent: () => `/admin/agents/roi`,
    roiAgentConfigNew: () => `/admin/agents/roi/new`,
    roiAgentConfigEdit: (configId = ':id') => `/admin/agents/roi/${configId}/edit`,
    roiAgentRuns: (configId = ':id') => `/admin/agents/roi/${configId}/runs`,
    roiAgentRunResults: (configId = ':configId', runId = ':runId') =>
      `/admin/agents/roi/${configId}/runs/${runId}/results`,
    roiAgentTestingGround: () => `/admin/agents/roi/testing-ground`,
    searchTestingGround: () => '/admin/agents/roi/search-testing-ground',
  },
};

export const queryStringifyUrl = (url: string, params: StringifiableRecord) =>
  queryString.stringifyUrl(
    {
      url,
      query: params,
    },
    {
      skipEmptyString: true,
      skipNull: true,
      arrayFormat: 'bracket',
    },
  );

export const parseQueryString = (paramName: string) => {
  const parsedParams = queryString.parse(window.location.search, {
    parseBooleans: true,
    parseNumbers: true,
    arrayFormat: 'bracket',
  });
  return parsedParams[paramName];
};

const uId = (universityId, universityFolderId) => {
  const params = universityId ? `?university_id=${universityId}` : '';
  return params && universityFolderId ? params.concat(`&university_folder_id=${universityFolderId}`) : params;
};

export default Urls;
